import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from './Header';
import { API_URL, BASE_URL, BROKEN_IMAGE_PLACEHOLDER, getImageUrl } from '../config';
import './OrderHistoryPage.css';

const OrderHistoryPage = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const detailPanelRef = useRef(null);
  const pendingScrollToDetail = useRef(false);

  const ordersApiUrl = `${API_URL}/Orders`;

  const getAuthHeader = () => {
    const token = localStorage.getItem('jwtToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${ordersApiUrl}/my-orders`, {
        headers: getAuthHeader()
      });
      const ordersData = Array.isArray(response.data) ? response.data : (response.data?.$values || []);
      setOrders(ordersData);
      setError(null);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('Failed to load order history. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedOrder || !pendingScrollToDetail.current) return;
    pendingScrollToDetail.current = false;
    if (typeof window === 'undefined' || !window.matchMedia('(max-width: 900px)').matches) return;
    let timeoutId;
    const frame = window.requestAnimationFrame(() => {
      timeoutId = window.setTimeout(() => {
        detailPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    });
    return () => {
      window.cancelAnimationFrame(frame);
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [selectedOrder]);

  const fetchOrderDetail = async (orderId) => {
    try {
      const response = await axios.get(`${ordersApiUrl}/my-orders/${orderId}`, {
        headers: getAuthHeader()
      });
      const orderData = response.data;
      // Normalize items array
      if (orderData.items && !Array.isArray(orderData.items)) {
        orderData.items = orderData.items.$values || [];
      }
      setSelectedOrder(orderData);
    } catch (err) {
      console.error('Error fetching order detail:', err);
      setError('Failed to load order details.');
    }
  };

  const formatCurrency = (val) => `₹${Number(val || 0).toFixed(2)}`;

  const printBill = () => {
    if (!selectedOrder) return;
    document.body.classList.add('printing-customer-bill');
    const done = () => document.body.classList.remove('printing-customer-bill');
    window.addEventListener('afterprint', done, { once: true });
    window.setTimeout(() => window.print(), 50);
  };

  const handleCancelOrder = async () => {
    if (!selectedOrder || !['Pending', 'Processing'].includes(selectedOrder.status)) return;
    if (!window.confirm('Are you sure you want to cancel this order?')) return;
    try {
      await axios.post(`${ordersApiUrl}/my-orders/${selectedOrder.orderId}/cancel`, {}, {
        headers: getAuthHeader()
      });
      setError(null);
      await fetchOrders();
      await fetchOrderDetail(selectedOrder.orderId);
    } catch (err) {
      setError(err.response?.data || 'Failed to cancel order. Please try again.');
    }
  };

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return 'status-pending';
      case 'processing': return 'status-processing';
      case 'shipped': return 'status-shipped';
      case 'outfordelivery': return 'status-shipped';
      case 'delivered': return 'status-delivered';
      case 'cancelled': return 'status-cancelled';
      case 'rejected': return 'status-rejected';
      default: return 'status-pending';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return (
          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
          </svg>
        );
      case 'processing':
        return (
          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"/>
          </svg>
        );
      case 'shipped':
      case 'outfordelivery':
        return (
          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
          </svg>
        );
      case 'delivered':
        return (
          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
          </svg>
        );
      case 'cancelled':
      case 'rejected':
        return (
          <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        );
      default:
        return null;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTrackingSteps = (status) => {
    const steps = [
      { key: 'pending', label: 'Order Placed', icon: 'placed' },
      { key: 'processing', label: 'Processing', icon: 'processing' },
      { key: 'shipped', label: 'Shipped', icon: 'shipped' },
      { key: 'delivered', label: 'Delivered', icon: 'delivered' }
    ];

    const statusOrder = ['pending', 'processing', 'shipped', 'delivered'];
    const s = status?.toLowerCase();
    const idx = statusOrder.indexOf(s);
    const currentIndex = idx >= 0 ? idx : (s === 'outfordelivery' ? 2 : 0);

    return steps.map((step, index) => ({
      ...step,
      completed: index <= currentIndex,
      current: index === currentIndex
    }));
  };

  if (loading) {
    return (
      <div className="order-history-wrapper">
        <Header />
        <div className="order-history-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading your orders...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="order-history-wrapper">
      <Header />
      <div className="order-history-container">
        <div className="order-history-header">
          <h1>My Orders</h1>
          <p>{orders.length} order{orders.length !== 1 ? 's' : ''} found</p>
        </div>

        {error && <div className="error-message">{error}</div>}

        {orders.length === 0 && !error ? (
          <div className="no-orders">
            <svg viewBox="0 0 24 24" fill="currentColor" width="80" height="80">
              <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
            </svg>
            <h3>No orders yet</h3>
            <p>You haven't placed any orders. Start shopping to see your orders here!</p>
            <button className="shop-now-btn" onClick={() => navigate('/')}>
              Shop Now
            </button>
          </div>
        ) : (
          <div className="orders-content">
            <div className="orders-list">
              {orders.map((order) => (
                <div
                  key={order.orderId}
                  className={`order-card ${selectedOrder?.orderId === order.orderId ? 'active' : ''}`}
                  onClick={() => {
                    pendingScrollToDetail.current = true;
                    fetchOrderDetail(order.orderId);
                  }}
                >
                  <div className="order-card-header">
                    <div className="order-id">
                      <span className="label">Order</span>
                      <span className="value">#{order.orderId}</span>
                    </div>
                    <span className={`order-status ${getStatusClass(order.status)}`}>
                      {getStatusIcon(order.status)}
                      {order.status}
                    </span>
                  </div>
                  <div className="order-card-body">
                    <div className="order-date">
                      <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                        <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM9 10H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm-8 4H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2z"/>
                      </svg>
                      {formatDate(order.orderDate)}
                    </div>
                    <div className="order-meta-row">
                      <span className="order-type-badge">{order.orderType === 'Offline' ? 'Offline' : 'Online'}</span>
                      {order.sellerName && (
                        <span className="order-seller">
                          Seller: {order.sellerName}
                          {(order.sellerCode || order.sellerId) ? ` (${order.sellerCode || order.sellerId})` : ''}
                        </span>
                      )}
                    </div>
                    <div className="order-points-row">
                      {order.pointsRedeemed > 0 && (
                        <span className="points-used">Points used: {order.pointsRedeemed}</span>
                      )}
                      {order.pointsEarned > 0 && (
                        <span className="points-earned">Points earned: {order.pointsEarned}</span>
                      )}
                      {(!order.pointsRedeemed && !order.pointsEarned) && (
                        <span className="points-none">—</span>
                      )}
                    </div>
                    <div className="order-summary">
                      <span className="items-count">{order.itemCount} item{order.itemCount !== 1 ? 's' : ''}</span>
                      <span className="order-total">₹{order.totalAmount?.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="order-detail-panel" ref={detailPanelRef} id="customer-order-detail">
              {selectedOrder ? (
                <>
                  <div className="detail-header">
                    <div className="detail-header-top">
                      <div className="detail-header-main">
                        <h2>Order #{selectedOrder.orderId}</h2>
                        <span className={`order-status large ${getStatusClass(selectedOrder.status)}`}>
                          {getStatusIcon(selectedOrder.status)}
                          {selectedOrder.status}
                        </span>
                        <span className="order-type-badge detail">{selectedOrder.orderType === 'Offline' ? 'Offline' : 'Online'}</span>
                      </div>
                      <div className="detail-header-actions no-print">
                        <button type="button" className="print-bill-btn" onClick={printBill}>
                          Print / Save bill
                        </button>
                        {['Pending', 'Processing'].includes(selectedOrder.status) && (
                          <button
                            type="button"
                            className="cancel-order-btn"
                            onClick={handleCancelOrder}
                          >
                            Cancel Order
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="detail-meta-grid">
                      {selectedOrder.sellerId != null && selectedOrder.sellerId !== '' && (
                        <div className="detail-meta-item">
                          <span className="detail-meta-label">Seller ID</span>
                          <span className="detail-meta-value">{selectedOrder.sellerCode || selectedOrder.sellerId}</span>
                        </div>
                      )}
                      {selectedOrder.sellerName && (
                        <div className="detail-meta-item">
                          <span className="detail-meta-label">Seller</span>
                          <span className="detail-meta-value">{selectedOrder.sellerName}</span>
                        </div>
                      )}
                      <div className="detail-meta-item">
                        <span className="detail-meta-label">Points used</span>
                        <span className="detail-meta-value">{selectedOrder.pointsRedeemed ?? 0}</span>
                      </div>
                      <div className="detail-meta-item">
                        <span className="detail-meta-label">Points earned</span>
                        <span className="detail-meta-value">{selectedOrder.pointsEarned ?? 0}</span>
                      </div>
                    </div>
                  </div>

                  <div className="tracking-section">
                    <h3>Order Tracking</h3>
                    {selectedOrder.status === 'Cancelled' || selectedOrder.status === 'Rejected' ? (
                      <p className="cancelled-tracking-note">
                        {selectedOrder.status === 'Rejected'
                          ? 'This order was rejected by the seller.'
                          : 'This order was cancelled.'}
                        {((selectedOrder.items || []).find((i) => i.deliveryNotes)?.deliveryNotes) && (
                          <> Comment: {(selectedOrder.items || []).find((i) => i.deliveryNotes)?.deliveryNotes}</>
                        )}
                      </p>
                    ) : (
                    <div className="tracking-timeline">
                      {(() => {
                        const steps = getTrackingSteps(selectedOrder.status);
                        return steps.map((step, index) => {
                          // Line should be green only if the NEXT step is also completed
                          const nextStepCompleted = index < steps.length - 1 && steps[index + 1].completed;
                          return (
                            <div
                              key={step.key}
                              className={`tracking-step ${step.completed ? 'completed' : ''} ${step.current ? 'current' : ''} ${nextStepCompleted ? 'line-complete' : ''}`}
                            >
                              <div className="step-indicator">
                                <div className="step-circle">
                                  {step.completed && (
                                    <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                                    </svg>
                                  )}
                                </div>
                                {index < 3 && <div className="step-line"></div>}
                              </div>
                              <span className="step-label">{step.label}</span>
                            </div>
                          );
                        });
                      })()}
                    </div>
                    )}
                  </div>

                  <div className="order-items-section">
                    <h3>Items in this order</h3>
                    <div className="order-items-list">
                      {selectedOrder.items?.map((item) => (
                        <div key={item.orderItemId} className="order-item-card">
                          <div className="order-item-main">
                            <img
                              src={getImageUrl(item.productImage)}
                              alt={item.productName}
                              className="item-image"
                              onError={(e) => { e.target.src = BROKEN_IMAGE_PLACEHOLDER; }}
                            />
                            <div className="item-details">
                              <h4 
                                className="item-name clickable"
                                onClick={() => navigate(`/product/${item.productId}`)}
                              >
                                {item.productName}
                              </h4>
                              <div className="item-meta">
                                <span className="item-qty">Qty: {item.quantity}</span>
                                <span className="item-price">₹{item.unitPrice?.toFixed(2)} each</span>
                                {item.sellerName && (
                                  <span className="item-seller">
                                    Seller: {item.sellerName}
                                    {(item.sellerCode || item.sellerId) ? ` (${item.sellerCode || item.sellerId})` : ''}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="item-total">
                              ₹{item.total?.toFixed(2)}
                              {Number(item.deliveryCharge) > 0 && (
                                <div className="item-delivery-charge">+ ₹{Number(item.deliveryCharge).toFixed(2)} delivery</div>
                              )}
                            </div>
                          </div>
                          <div className="item-delivery-info">
                            <div className="delivery-status-row">
                              <span className={`item-delivery-status ${item.deliveryStatus?.toLowerCase() || 'pending'}`}>
                                {item.deliveryStatus || 'Pending'}
                              </span>
                              {item.trackingNumber && (
                                <span className="tracking-number">
                                  Tracking: {item.trackingNumber}
                                </span>
                              )}
                            </div>
                            <div className="delivery-date-row">
                              {item.deliveryStatus === 'Delivered' && item.actualDeliveryDate ? (
                                <span className="delivered-date">
                                  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                                  </svg>
                                  Delivered on {new Date(item.actualDeliveryDate).toLocaleDateString('en-IN', { 
                                    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' 
                                  })}
                                </span>
                              ) : (() => {
                                const expectedDate = item.expectedDeliveryDate || (selectedOrder?.orderDate 
                                  ? new Date(new Date(selectedOrder.orderDate).getTime() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
                                  : null);
                                return expectedDate ? (
                                  <span className="expected-date">
                                    <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                                      <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM9 10H7v2h2v-2zm4 0h-2v2h2v-2z"/>
                                    </svg>
                                    Expected by {new Date(expectedDate).toLocaleDateString('en-IN', { 
                                      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' 
                                    })}
                                  </span>
                                ) : (
                                  <span className="no-date">Delivery date will be updated soon</span>
                                );
                              })()}
                            </div>
                            {item.deliveryNotes && (
                              <div className="delivery-notes">
                                <span className="notes-label">Note:</span> {item.deliveryNotes}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="customer-bill-print" id="customer-bill-print">
                    <div className="bill-header no-print">
                      <h3>Bill</h3>
                      <button type="button" className="print-bill-btn" onClick={printBill}>
                        Print / Save bill
                      </button>
                    </div>
                    <div className="customer-invoice">
                      <div className="invoice-top">
                        <div>
                          <h2>Yelooo</h2>
                          <p className="invoice-tag">Tax Invoice / Bill</p>
                        </div>
                        <div className="invoice-meta">
                          <strong>ORD-{selectedOrder.orderId}</strong>
                          <span>{formatDate(selectedOrder.orderDate)}</span>
                          <span>{selectedOrder.status}</span>
                        </div>
                      </div>
                      <div className="invoice-parties">
                        <div>
                          <h4>Sold by</h4>
                          <p>{selectedOrder.sellerShop?.shopName || selectedOrder.sellerName || 'Yelooo'}</p>
                          {(selectedOrder.sellerShop?.sellerCode || selectedOrder.sellerId) && (
                            <p>Seller ID: {selectedOrder.sellerShop?.sellerCode || selectedOrder.sellerId}</p>
                          )}
                          {selectedOrder.sellerShop?.gstNumber && <p>GSTIN: {selectedOrder.sellerShop.gstNumber}</p>}
                          {selectedOrder.sellerShop?.phone && <p>Phone: {selectedOrder.sellerShop.phone}</p>}
                          <p>
                            {[selectedOrder.sellerShop?.address, selectedOrder.sellerShop?.city, selectedOrder.sellerShop?.state, selectedOrder.sellerShop?.pinCode]
                              .filter(Boolean).join(', ')}
                          </p>
                        </div>
                        <div>
                          <h4>Bill to</h4>
                          <p>{selectedOrder.customer?.name || 'Customer'}</p>
                          {selectedOrder.customer?.userCode && <p>Customer ID: {selectedOrder.customer.userCode}</p>}
                          {selectedOrder.customer?.phone && <p>Phone: {selectedOrder.customer.phone}</p>}
                          {selectedOrder.customer?.email && <p>{selectedOrder.customer.email}</p>}
                          <p>
                            {[selectedOrder.customer?.address, selectedOrder.customer?.addressLine2, selectedOrder.customer?.city, selectedOrder.customer?.state, selectedOrder.customer?.pinCode]
                              .filter(Boolean).join(', ')}
                          </p>
                        </div>
                      </div>
                      <table className="invoice-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Item</th>
                            <th>Qty</th>
                            <th>Price</th>
                            <th>Delivery</th>
                            <th>Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(selectedOrder.items || []).map((item, idx) => (
                            <tr key={item.orderItemId || idx}>
                              <td>{idx + 1}</td>
                              <td>
                                {item.productName}
                                {(item.sizeName || item.colorName) && (
                                  <div className="bill-item-seller">
                                    {[item.sizeName && `Size: ${item.sizeName}`, item.colorName && `Color: ${item.colorName}`].filter(Boolean).join(' · ')}
                                  </div>
                                )}
                                {item.sellerId ? <div className="bill-item-seller">Seller ID: {item.sellerId}</div> : null}
                              </td>
                              <td>{item.quantity}</td>
                              <td>{formatCurrency(item.unitPrice)}</td>
                              <td>{formatCurrency(item.deliveryCharge)}</td>
                              <td>{formatCurrency(item.total)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className="invoice-totals">
                        <div>
                          <span>Items</span>
                          <span>{formatCurrency(selectedOrder.itemsSubtotal ?? selectedOrder.ItemsSubtotal ?? (selectedOrder.items || []).reduce((s, i) => s + (Number(i.total) || 0), 0))}</span>
                        </div>
                        <div>
                          <span>Delivery Charges</span>
                          <span>{(Number(selectedOrder.deliveryCharge) || 0) > 0 ? formatCurrency(selectedOrder.deliveryCharge) : 'FREE'}</span>
                        </div>
                        {(Number(selectedOrder.benefitDiscountAmount) || 0) > 0 && (
                          <div><span>Points benefit</span><span>− {formatCurrency(selectedOrder.benefitDiscountAmount)}</span></div>
                        )}
                        {(Number(selectedOrder.voucherDiscountAmount) || 0) > 0 && (
                          <div><span>Voucher</span><span>− {formatCurrency(selectedOrder.voucherDiscountAmount)}</span></div>
                        )}
                        {(Number(selectedOrder.pointsDiscountAmount) || 0) > 0 && (
                          <div><span>Points discount</span><span>− {formatCurrency(selectedOrder.pointsDiscountAmount)}</span></div>
                        )}
                        <div className="grand">
                          <span>Order Total</span>
                          <span>{formatCurrency(selectedOrder.totalAmount)}</span>
                        </div>
                      </div>
                      <p className="invoice-foot">This is a computer-generated bill. Use Print / Save bill to print or save as PDF.</p>
                    </div>
                  </div>
                </>
              ) : (
                <div className="no-selection">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="60" height="60">
                    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
                  </svg>
                  <p>Select an order to view details</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderHistoryPage;
